import { filterContentByStore } from 'help/utils/contentSelectors/contentSelectors'
import { toKebabCase } from 'help/utils/toKebabCase/toKebabCase'
import {
	REMOVE_DOUBLESPACE,
	STRIP_HTML,
} from 'utils/contentStack/constants/regex'
import { normalizeText } from 'utils/contentStack/normalizations/normalizeText'

import type {
	ButtonQuery,
	ButtonQueryContent,
	FAQsQueryContent,
	FAQsQueryQuestion,
	FAQsQueryQuestionContent,
	FAQsQueryResult,
	Question,
	QuestionDetailQueryResult,
	QuestionSEOInfo,
	QuestionsByCategory,
	QuestionsQueryContent,
	QuestionsQueryResult,
} from './types'

const SEO_DESCRIPTION_LENGTH = 150

const createQuestionUrl = (category: string, questionKey: string) =>
	`/help/${category}/${questionKey}`

const normalizeQuestion = (
	categoryKey: string,
	question: FAQsQueryQuestion,
	store: string
): Question => {
	const { questionKey, system } = question
	const { content } = filterContentByStore<FAQsQueryQuestionContent>(
		question,
		store
	)

	return {
		uid: system.uid,
		title: content.question ?? '',
		key: questionKey,
		url: createQuestionUrl(toKebabCase(categoryKey), toKebabCase(questionKey)),
		subTitle: content.isSustainable
			? {
					icon: 'sustainability',
					textLabel: 'help.sustainable.label',
				}
			: null,
	}
}

export const normalizeFrequentlyAskedQuestions = (
	data: FAQsQueryResult,
	store: string
) => {
	const {
		content: { relatedQuestions },
	} = filterContentByStore<FAQsQueryContent>(data.allFaq.items[0], store)

	const questions = relatedQuestions.map(
		({
			relatedCategory: {
				edges: [
					{
						node: { title: categoryKey },
					},
				],
			},

			relatedQuestion: {
				edges: [{ node }],
			},
		}) => normalizeQuestion(categoryKey, node, store)
	)

	return {
		questions,
	}
}

export const normalizeQuestionsByCategory = (
	data: QuestionsQueryResult,
	store: string,
	categoryKey: string
): QuestionsByCategory => {
	if (data.allCategory.total !== 1) {
		throw new Error('Category not found')
	}

	const { content } = filterContentByStore<QuestionsQueryContent>(
		data.allCategory.items[0],
		store
	)

	const { relatedQuestions, commonRelatedQuestions, title } = content

	const normalizedQuestions = relatedQuestions.edges.map(({ node }) =>
		normalizeQuestion(categoryKey, node, store)
	)

	const normalizedCommonQuestions = commonRelatedQuestions.edges.map(
		({ node }) => normalizeQuestion(categoryKey, node, store)
	)

	return {
		category: {
			title,
		},
		questions: [...normalizedCommonQuestions, ...normalizedQuestions],
		totalQuestions: relatedQuestions.totalCount,
	}
}

export const normalizeQuestionById = (
	data: QuestionDetailQueryResult,
	store: string,
	categoryKey: string
) => {
	const questionItem = data.allQuestion.items[0]
	const { questionKey } = questionItem

	const { content } = filterContentByStore(questionItem, store)
	const {
		question,
		isSustainable,
		answers,
		relatedQuestions,
		commonRelatedQuestions,
	} = content

	const questionObj = {
		categoryKey,
		key: questionKey,
		title: question,
		isSustainable: isSustainable || false,
	}

	const normalizedAnswers = answers.map(
		({ text, buttons: { edges } }, index) => ({
			text: normalizeText(text),
			buttons: normalizeButtons(edges, store),
			indexKey: index,
		})
	)

	const normalizedRelatedQuestions = relatedQuestions.edges.map(({ node }) =>
		normalizeQuestion(categoryKey, node, store)
	)
	const normalizedCommonRelatedQuestions = commonRelatedQuestions.edges.map(
		({ node }) => normalizeQuestion(categoryKey, node, store)
	)

	return {
		question: questionObj,
		answers: normalizedAnswers,
		relatedQuestions: [
			...normalizedRelatedQuestions,
			...normalizedCommonRelatedQuestions,
		],
	}
}

const normalizeButtons = (
	buttons: {
		node: ButtonQuery
	}[],
	store: string
) =>
	buttons
		.filter((btn) => !!btn.node.buttonKey)
		.map(({ node }: { node: ButtonQuery }) => {
			const {
				content,
				content: { labelKey, label, linkInNewTab, actions, isLegacy },
				buttonKey,
			} = node

			return {
				label,
				labelKey,
				isLegacy,
				actions,
				href: getButtonHref(content, store),
				target: linkInNewTab ? '_blank' : undefined,
				rel: linkInNewTab ? 'noopener noreferrer' : null,
				key: buttonKey,
			}
		})

const getButtonHref = (btnContent: ButtonQueryContent, store: string) => {
	const { outletLoggedLink, loggedLink, outletLink, link } = btnContent
	const loggedLinkAttr =
		store === 'outlet' && outletLoggedLink?.href
			? outletLoggedLink?.href
			: loggedLink?.href
	const linkAttr =
		store === 'outlet' && outletLink?.href ? outletLink?.href : link?.href

	return {
		linkAttr: linkAttr || '',
		loggedLinkAttr: loggedLinkAttr || '',
	}
}

export const normalizeQuestionSEOInfo = (
	data: QuestionDetailQueryResult,
	store: string
): QuestionSEOInfo => {
	const questionItem = data.allQuestion.items[0]
	const { content } = filterContentByStore(questionItem, store)
	return {
		title: content.question,
		description:
			content?.answers
				?.map((answer) => answer.text)
				?.join()
				.replace(STRIP_HTML, ' ')
				.replace(REMOVE_DOUBLESPACE, ' ')
				.trim()
				.substring(0, SEO_DESCRIPTION_LENGTH) ?? '',
	}
}
